import { LoadingOutlined } from '@ant-design/icons'
import {
  Accordion,
  Drawer,
  LabeledValue,
  Layout,
  Tag,
  Text,
  Timeline,
} from '@loadsmart/miranda-react'
import { Spin } from 'antd/lib'
import { useEffect, useState } from 'react'
import styled from 'styled-components'

import { useAsset } from '../../hooks/useAsset'
import {
  getAttachmentDetails,
  getTruckClass,
  getTruckFuelType,
  getAssetDwellTime,
} from '../../utils/reports'
import { getAssetData } from '../../../clients/tools'
import { AssetData, ViewableImage } from '../../../models/client/reports'

import { DeleteAssetDialog } from '../DeleteAssetDialog'
import { OutgateAssetDialog } from '../OutgateAssetDialog'
import { utcToDisplayDate, utcToMilitaryDisplayTime } from '../../../util/datetime'
import { RenderImage } from '../RenderImage'

const StyledDrawerBody = styled(Drawer.Body)`
  display: grid;
  text-align: center;
  align-items: center;
  height: 100%;
`

type ViewAssetDrawerProps = {
  open: boolean
  handleClose: () => void
  onViewImage?: (image: ViewableImage) => void
  onEdit: () => void
  asset: any
  refresh: () => void
}

type OutgateAssetEventProps = {
  outgatedAsset: any
  assetType: string
  timezone: string
}

function OutgateAssetEvent({ outgatedAsset, assetType, timezone }: OutgateAssetEventProps) {
  return (
    <Layout.Stack>
      <Text>
        <Text variant="body-md-bold">{assetType}</Text> Outgate
      </Text>
      <Timeline>
        <Timeline.Item>
          <Timeline.ItemHeader>
            <Timeline.ItemHeaderTitle>
              <Layout.Stack>
                <Text>
                  {utcToDisplayDate(outgatedAsset.detectedAt, timezone)} -{' '}
                  {utcToMilitaryDisplayTime(outgatedAsset.detectedAt, timezone)}
                </Text>
                <Text>
                  Outgate -{' '}
                  {outgatedAsset.tags?.length ? 'Manual transaction' : 'Automatic transaction'}
                </Text>
              </Layout.Stack>
            </Timeline.ItemHeaderTitle>
          </Timeline.ItemHeader>
        </Timeline.Item>
      </Timeline>
    </Layout.Stack>
  )
}

export function ViewAssetDrawer({
  open,
  handleClose,
  onViewImage,
  onEdit,
  asset,
  refresh,
}: ViewAssetDrawerProps) {
  const { getObservationById } = useAsset({})

  const [isOutgateDialogOpen, setIsOutgateDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [assetData, setAssetData] = useState<AssetData>({})
  const [attachmentData, setAttachmentData] = useState([])

  const canOutgateVehicle = !assetData.vehicleOutwardObservationId && Boolean(assetData.vehicleId)
  const canOutgateLoad = !assetData.loadOutwardObservationId && Boolean(assetData.loadId)
  const canOutgateChassis = !assetData.chassisOutwardObservationId && Boolean(assetData.chassisId)
  const canOutgateObservation = assetData.direction

  const canOutgateAsset =
    canOutgateObservation && (canOutgateChassis || canOutgateLoad || canOutgateVehicle)

  const handleOutgateClose = () => {
    setIsOutgateDialogOpen(false)
  }

  const handleDeleteClose = () => {
    setIsDeleteDialogOpen(!isDeleteDialogOpen)
  }

  const fetchObservation = async () => {
    setIsLoading(true)
    const response = await getObservationById(asset)
    const assetData = getAssetData(response)

    setAssetData(response)
    setAttachmentData(assetData)

    setIsLoading(false)
  }

  useEffect(() => {
    if (asset && open) {
      fetchObservation()
    }
  }, [asset, open])

  if (isLoading || !assetData) {
    return (
      <Drawer open={open} onClose={handleClose} size="medium">
        <Drawer.Header>
          <Text variant="heading-md-bold">Asset details</Text>
          <Drawer.Close />
        </Drawer.Header>

        <StyledDrawerBody>
          <Layout.Stack gap="spacing-5">
            <Spin indicator={<LoadingOutlined spin />} size="large" />
            <Text>Loading asset...</Text>
          </Layout.Stack>
        </StyledDrawerBody>
      </Drawer>
    )
  }

  return (
    <>
      <Drawer open={open} onClose={handleClose} size="medium">
        <Drawer.Header>
          <Text variant="heading-md-bold">Asset details</Text>
          <Drawer.Close />
        </Drawer.Header>

        <Drawer.Body>
          <Layout.Stack>
            <RenderImage observation={assetData} onViewImage={onViewImage} />
            <Accordion expanded>
              <Accordion.Title>Gate transactions</Accordion.Title>
              <Accordion.Content>
                <Layout.Stack>
                  <Layout.Stack>
                    {/* <Text>{assetData.direction ? 'Ingate' : 'Outgate'}</Text> */}
                    <Timeline>
                      <Timeline.Item>
                        <Timeline.ItemHeader>
                          <Timeline.ItemHeaderTitle>
                            <Layout.Stack>
                              <Text>
                                {utcToDisplayDate(
                                  assetData.detectedAt,
                                  assetData.gate?.site?.timezone
                                )}{' '}
                                -{' '}
                                {utcToMilitaryDisplayTime(
                                  assetData.detectedAt,
                                  assetData.gate?.site?.timezone
                                )}
                              </Text>
                              <Text>
                                {assetData.direction ? 'Ingate' : 'Outgate'} -{' '}
                                {assetData.tags?.length
                                  ? 'Manual transaction'
                                  : 'Automatic transaction'}
                              </Text>
                            </Layout.Stack>
                          </Timeline.ItemHeaderTitle>
                        </Timeline.ItemHeader>
                        {attachmentData.length ? (
                          <Timeline.ItemBody>
                            <Layout.Stack gap="spacing-1">
                              <Text variant="body-md-bold">Ingated with</Text>
                              {attachmentData?.map((attachment) => (
                                <Layout.Group align="center" gap="spacing-1" key={attachment.id}>
                                  <Text variant="body-md" key={attachment.id}>
                                    {getAttachmentDetails(attachment)}
                                  </Text>
                                  {attachment.isOutgated ? (
                                    <Tag size="small" variant="warning">
                                      Outgated
                                    </Tag>
                                  ) : null}
                                </Layout.Group>
                              ))}
                            </Layout.Stack>
                          </Timeline.ItemBody>
                        ) : null}
                      </Timeline.Item>
                    </Timeline>
                  </Layout.Stack>
                  {assetData.vehicleOutwardObservation && (
                    <OutgateAssetEvent
                      outgatedAsset={assetData.vehicleOutwardObservation}
                      timezone={assetData.gate?.site?.timezone}
                      assetType="Vehicle"
                    />
                  )}
                  {assetData.chassisOutwardObservation && (
                    <OutgateAssetEvent
                      outgatedAsset={assetData.chassisOutwardObservation}
                      timezone={assetData.gate?.site?.timezone}
                      assetType="Chassis"
                    />
                  )}
                  {assetData.loadOutwardObservation && (
                    <OutgateAssetEvent
                      outgatedAsset={assetData.loadOutwardObservation}
                      timezone={assetData.gate?.site?.timezone}
                      assetType="Load"
                    />
                  )}
                </Layout.Stack>
              </Accordion.Content>
            </Accordion>

            <Accordion expanded>
              <Accordion.Title>Vehicle details</Accordion.Title>
              <Accordion.Content>
                <Layout.Stack>
                  <Layout.Grid>
                    <LabeledValue>
                      <LabeledValue.Label>Location</LabeledValue.Label>
                      <LabeledValue.Value>{assetData.gate?.site?.name || '-'}</LabeledValue.Value>
                    </LabeledValue>
                    <LabeledValue>
                      <LabeledValue.Label>Vehicle type</LabeledValue.Label>
                      <LabeledValue.Value>
                        {assetData.vehicle?.type?.name || '-'}
                      </LabeledValue.Value>
                    </LabeledValue>
                    <LabeledValue>
                      <LabeledValue.Label>Dwell time</LabeledValue.Label>
                      <LabeledValue.Value>
                        {getAssetDwellTime(assetData.vehicleDwellTime)}
                      </LabeledValue.Value>
                    </LabeledValue>
                  </Layout.Grid>

                  <Layout.Grid>
                    <LabeledValue>
                      <LabeledValue.Label>Carrier</LabeledValue.Label>
                      <LabeledValue.Value>
                        {assetData.vehicle?.carrier?.name || '-'}
                      </LabeledValue.Value>
                    </LabeledValue>
                    <LabeledValue>
                      <LabeledValue.Label>Truck number</LabeledValue.Label>
                      <LabeledValue.Value>{assetData.vehicle?.number || '-'}</LabeledValue.Value>
                    </LabeledValue>
                    <LabeledValue>
                      <LabeledValue.Label>License plate</LabeledValue.Label>
                      <LabeledValue.Value>
                        {assetData.vehicle?.licensePlate || '-'}
                      </LabeledValue.Value>
                    </LabeledValue>
                  </Layout.Grid>

                  <Layout.Grid>
                    <LabeledValue>
                      <LabeledValue.Label>DOT</LabeledValue.Label>
                      <LabeledValue.Value>{assetData.vehicle?.dotNumber || '-'}</LabeledValue.Value>
                    </LabeledValue>
                    <LabeledValue>
                      <LabeledValue.Label>Truck class</LabeledValue.Label>
                      <LabeledValue.Value>
                        {getTruckClass(assetData.vehicle?.weightClass)}
                      </LabeledValue.Value>
                    </LabeledValue>
                    <LabeledValue>
                      <LabeledValue.Label>Fuel type</LabeledValue.Label>
                      <LabeledValue.Value>
                        {getTruckFuelType(assetData.vehicle?.fuelType)}
                      </LabeledValue.Value>
                    </LabeledValue>
                  </Layout.Grid>
                </Layout.Stack>
              </Accordion.Content>
            </Accordion>

            <Accordion expanded>
              <Accordion.Title>Chassis details</Accordion.Title>
              <Accordion.Content>
                <Layout.Stack>
                  <Layout.Grid>
                    <LabeledValue>
                      <LabeledValue.Label>Number</LabeledValue.Label>
                      <LabeledValue.Value>{assetData.chassis?.number || '-'}</LabeledValue.Value>
                    </LabeledValue>
                    <LabeledValue>
                      <LabeledValue.Label>License plate</LabeledValue.Label>
                      <LabeledValue.Value>
                        {assetData.chassis?.licensePlate || '-'}
                      </LabeledValue.Value>
                    </LabeledValue>
                    <LabeledValue>
                      <LabeledValue.Label>Dwell time</LabeledValue.Label>
                      <LabeledValue.Value>
                        {getAssetDwellTime(assetData.chassisDwellTime)}
                      </LabeledValue.Value>
                    </LabeledValue>
                  </Layout.Grid>
                </Layout.Stack>
              </Accordion.Content>
            </Accordion>

            <Accordion expanded>
              <Accordion.Title>Load details</Accordion.Title>
              <Accordion.Content>
                <Layout.Stack>
                  <Layout.Grid>
                    <LabeledValue>
                      <LabeledValue.Label>Load type</LabeledValue.Label>
                      <LabeledValue.Value>{assetData.load?.type?.name || '-'}</LabeledValue.Value>
                    </LabeledValue>
                    <LabeledValue>
                      <LabeledValue.Label>Load number</LabeledValue.Label>
                      <LabeledValue.Value>{assetData.load?.number || '-'}</LabeledValue.Value>
                    </LabeledValue>
                    <LabeledValue>
                      <LabeledValue.Label>Dwell time</LabeledValue.Label>
                      <LabeledValue.Value>
                        {getAssetDwellTime(assetData.loadDwellTime)}
                      </LabeledValue.Value>
                    </LabeledValue>
                  </Layout.Grid>

                  <Layout.Grid>
                    <LabeledValue>
                      <LabeledValue.Label>Has seal</LabeledValue.Label>
                      <LabeledValue.Value>{assetData.hasSeal ? 'Yes' : 'No'}</LabeledValue.Value>
                    </LabeledValue>
                    <LabeledValue>
                      <LabeledValue.Label>Seal number</LabeledValue.Label>
                      <LabeledValue.Value>{assetData.sealNumber || '-'}</LabeledValue.Value>
                    </LabeledValue>
                    <LabeledValue>
                      <LabeledValue.Label>License plate</LabeledValue.Label>
                      <LabeledValue.Value>{assetData.load?.licensePlate || '-'}</LabeledValue.Value>
                    </LabeledValue>
                  </Layout.Grid>
                  <Layout.Grid>
                    <LabeledValue>
                      <LabeledValue.Label>BCO</LabeledValue.Label>
                      <LabeledValue.Value>{assetData.load?.bco || '-'}</LabeledValue.Value>
                    </LabeledValue>
                  </Layout.Grid>
                </Layout.Stack>
              </Accordion.Content>
            </Accordion>
          </Layout.Stack>
        </Drawer.Body>

        <Drawer.Actions>
          <Layout.Group justify="space-between" style={{ width: '100%' }}>
            <Layout.Group>
              <Drawer.ActionTertiary
                variant="danger"
                onClick={() => setIsDeleteDialogOpen(!isDeleteDialogOpen)}
              >
                Delete
              </Drawer.ActionTertiary>
            </Layout.Group>
            <Layout.Group>
              <Drawer.ActionSecondary onClick={onEdit}>Edit details</Drawer.ActionSecondary>
              <Drawer.ActionPrimary
                onClick={() => setIsOutgateDialogOpen(!isOutgateDialogOpen)}
                disabled={!canOutgateAsset}
              >
                Outgate
              </Drawer.ActionPrimary>
            </Layout.Group>
          </Layout.Group>
        </Drawer.Actions>
      </Drawer>
      <OutgateAssetDialog
        isOpen={isOutgateDialogOpen}
        handleClose={handleOutgateClose}
        handleAssetDrawerClose={handleClose}
        asset={assetData}
        refresh={refresh}
      />
      <DeleteAssetDialog
        isOpen={isDeleteDialogOpen}
        handleClose={handleDeleteClose}
        handleAssetDrawerClose={handleClose}
        asset={assetData}
        refresh={refresh}
      />
    </>
  )
}
